import React, { useEffect, useRef, useState } from "react";
import { refreshAOS } from "../utils/aosInit";
import "./VarahaPopup.css";
import { setPopupClosedThisSession } from "../utils/popupUtils";

const festivalImg = "/assets/varaha-festival.jpeg";

/* === Inline Rangoli SVGs (kept in this one file for convenience) === */
function RangoliCorner({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 120 120"
      aria-hidden="true"
      role="img"
    >
      <defs>
        <linearGradient id="rg-gold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f7d37b" />
          <stop offset="100%" stopColor="#c99c2e" />
        </linearGradient>
      </defs>
      <g
        fill="none"
        stroke="url(#rg-gold)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M10 110 Q 64 56 110 10" />
        <circle cx="20" cy="100" r="3" />
        <circle cx="35" cy="85" r="2.5" />
        <circle cx="50" cy="70" r="2.5" />
        <circle cx="65" cy="55" r="2" />
        <circle cx="80" cy="40" r="2" />
        <circle cx="95" cy="25" r="1.8" />
        <path d="M24 96 l8 -8" />
        <path d="M39 81 l8 -8" />
        <path d="M54 66 l8 -8" />
        <path d="M69 51 l8 -8" />
        <path d="M84 36 l8 -8" />
        <g>
          <path d="M97 17 q8 2 11 -6 q-9 1 -11 6z" />
          <path d="M86 28 q8 2 11 -6 q-9 1 -11 6z" />
          <path d="M75 39 q8 2 11 -6 q-9 1 -11 6z" />
        </g>
      </g>
    </svg>
  );
}

function RangoliMandala({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 200 200"
      aria-hidden="true"
      role="img"
    >
      <defs>
        <radialGradient id="rg-magenta" r="60%">
          <stop offset="0%" stopColor="#f6e3f2" />
          <stop offset="100%" stopColor="#a10f7a" />
        </radialGradient>
      </defs>
      <g fill="none" stroke="url(#rg-magenta)" strokeWidth="2">
        <circle cx="100" cy="100" r="70" />
        <circle cx="100" cy="100" r="54" />
        <circle cx="100" cy="100" r="38" />
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i * Math.PI * 2) / 12;
          const x = 100 + Math.cos(a) * 70;
          const y = 100 + Math.sin(a) * 70;
          const x2 = 100 + Math.cos(a) * 38;
          const y2 = 100 + Math.sin(a) * 38;
          return (
            <g key={i}>
              <line x1="100" y1="100" x2={x} y2={y} />
              <path d={`M${x2} ${y2} q10 -10 20 0 q-10 10 -20 0z`} />
            </g>
          );
        })}
        {Array.from({ length: 24 }).map((_, i) => {
          const a = (i * Math.PI * 2) / 24;
          const x = 100 + Math.cos(a) * 54;
          const y = 100 + Math.sin(a) * 54;
          return <circle key={`c-${i}`} cx={x} cy={y} r="2" />;
        })}
      </g>
    </svg>
  );
}

function RangoliDivider({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 800 60"
      aria-hidden="true"
      role="img"
    >
      <defs>
        <linearGradient id="rg-divider" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#a10f7a" />
          <stop offset="100%" stopColor="#c99c2e" />
        </linearGradient>
      </defs>
      <g fill="none" stroke="url(#rg-divider)" strokeWidth="2">
        <path d="M10 30 Q 110 5 210 30 T 410 30 T 610 30 T 790 30" />
        {Array.from({ length: 9 }).map((_, i) => {
          const x = 100 + i * 80;
          return (
            <g key={i}>
              <path d={`M${x} 30 l12 -12 l12 12 l-12 12 Z`} />
              <circle cx={x} cy={30} r="2.5" />
            </g>
          );
        })}
      </g>
    </svg>
  );
}

function RangoliDiya({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 120 120"
      aria-hidden="true"
      role="img"
    >
      <defs>
        <linearGradient id="rg-flame" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffd27a" />
          <stop offset="100%" stopColor="#ff7b00" />
        </linearGradient>
        <linearGradient id="rg-diya" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#c99c2e" />
          <stop offset="100%" stopColor="#8a6b5e" />
        </linearGradient>
      </defs>
      <g fill="none" stroke="#a10f7a" strokeWidth="1.5">
        <path fill="url(#rg-diya)" d="M20 70q40 40 80 0q-40 10 -80 0z" />
        <path d="M20 70q40 40 80 0" />
        <path fill="url(#rg-flame)" d="M60 30 q14 22 0 32 q-14 -10 0 -32z" />
        <circle cx="60" cy="72" r="3" fill="#c99c2e" />
        {Array.from({ length: 10 }).map((_, i) => (
          <circle key={i} cx={24 + i * 8} cy={78 + Math.sin(i) * 1.5} r="1.6" />
        ))}
      </g>
    </svg>
  );
}
/* === end inline SVGs === */

export default function VarahaFestivalPopup({
  scheduleMs,
  cooldownDays = 3,
  bypassCooldown = false,
  debug = false,
  title = "Varaha Silks Grand Festival Sale is here!",
  subtitle = "Shop your festive favorites today…",
  poweredBy = "Powered by Varaha Silks",
  onSubmit,
}) {
  const [open, setOpen] = useState(false);
  const timersRef = useRef([]);
  const closeBtnRef = useRef(null);
  const prevFocusRef = useRef(null);
  const COOLDOWN_KEY = "vs_popup_cooldown_at";

  // default relative intervals: 5s, then +30s, then +60s → 5s, 35s, 95s
  const relativeIntervals =
    Array.isArray(scheduleMs) && scheduleMs.length
      ? scheduleMs
      : [5000, 30000, 60000];

  const cumulativeTimes = (() => {
    const out = [];
    let sum = 0;
    for (const ms of relativeIntervals) {
      sum += Math.max(0, Number(ms) || 0);
      out.push(sum);
    }
    return out;
  })();

  const daysSince = (ts) => (Date.now() - ts) / (1000 * 60 * 60 * 24);
  const canStartSchedule = () => {
    if (bypassCooldown) return true;
    const ts = Number(sessionStorage.getItem(COOLDOWN_KEY) || 0);
    return !ts || daysSince(ts) >= cooldownDays;
  };

  // AOS is already initialized globally, no need to initialize again

  // schedule chained openings (StrictMode-safe; first pass cleans, second pass sets final timers)
  useEffect(() => {
    const okToRun = canStartSchedule();
    // Log for debugging popup issues (only in development)
    if (process.env.NODE_ENV === 'development') {
      // Popup schedule check
    }
    
    if (debug) {
      // Debug mode enabled
    }
    
    if (!okToRun) return;

    sessionStorage.setItem(COOLDOWN_KEY, String(Date.now()));

    timersRef.current = cumulativeTimes.map((atMs) =>
      setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {}
        if (debug)setOpen(true);
        setTimeout(() => {
          refreshAOS();
        }, 0);
      }, atMs)
    );

    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debug, bypassCooldown, cooldownDays, JSON.stringify(relativeIntervals)]);

  // body lock + inert background + focus trap entry
  useEffect(() => {
    const html = document.documentElement;
    const appShell = document.querySelector(".app-shell");

    if (open) {
      prevFocusRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      if (prevFocusRef.current?.blur) prevFocusRef.current.blur();

      html.style.overflow = "hidden";
      if (appShell) appShell.setAttribute("inert", "");

      setTimeout(() => closeBtnRef.current?.focus(), 0);
    } else {
      html.style.overflow = "";
      if (appShell) appShell.removeAttribute("inert");
      if (prevFocusRef.current?.focus) prevFocusRef.current.focus();
    }

    return () => {
      html.style.overflow = "";
      if (appShell) appShell.removeAttribute("inert");
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const handleClose = () => {
    // Mark popup as closed for this session
    setPopupClosedThisSession();
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const fd = new FormData(e.currentTarget);
    const data = {
      countryCode: (fd.get("countryCode") || "").toString(),
      whatsapp: (fd.get("whatsapp") || "").toString().trim(),
      email: (fd.get("email") || "").toString().trim(),
    };
    
    // Validate data before submitting
    if (!data.email || !data.whatsapp) {
      alert("Please fill in both email and WhatsApp number.");
      return;
    }
    
    try {
      if (onSubmit) {
        await onSubmit(data);
      }
    } catch (error) {
      // Error handling - newsletter signup failure shouldn't block form submission
    } finally {
      // closing does NOT cancel future scheduled opens
      handleClose();
    }
  };

  if (!open) return null;

  return (
    <div
      className="vs-modal vs-open"
      role="dialog"
      aria-modal="true"
      aria-label="Varaha Silks Offer"
    >
      {/* backdrop */}
      <button
        type="button"
        aria-label="Close popup"
        className="vs-backdrop"
        onClick={handleClose}
      />

      <div className="vs-dialog">
        {/* decorative layer BEHIND the card */}
        <div className="vs-decor" aria-hidden="true">
          <RangoliCorner className="rg rg--tl" />
          <RangoliCorner className="rg rg--br" />
          <RangoliMandala className="rg rg--mandala" />
          <RangoliDiya className="rg rg--diya" />
        </div>

        <div className="vs-card" data-aos="zoom-in">
          <button
            type="button"
            className="vs-close"
            aria-label="Close"
            onClick={handleClose}
            ref={closeBtnRef}
          >
            ×
          </button>

          {/* optional divider inside the card header */}
          <RangoliDivider className="rg rg--divider" />

          <div className="vs-dialog-grid">
            <div className="vs-left" data-aos="fade-right">
              <img
                src={festivalImg}
                alt="Varaha Silks Festival"
                loading="lazy"
              />
            </div>

            <div className="vs-right" data-aos="fade-left" data-aos-delay="100">
              <h2 className="vs-title">{title}</h2>
              <p className="vs-sub">{subtitle}</p>

              <form className="vs-form" onSubmit={handleSubmit}>
                <label
                  className="vs-field"
                  data-aos="fade-up"
                  data-aos-delay="150"
                >
                  <span>WhatsApp Number</span>
                  <div className="vs-phone">
                    <div className="vs-cc">
                      <select
                        name="countryCode"
                        className="vs-cc-select"
                        defaultValue="+91"
                        aria-label="Country code"
                      >
                        <option value="+91">🇮🇳 +91</option>
                        <option value="+1">🇺🇸 +1</option>
                        <option value="+44">🇬🇧 +44</option>
                        <option value="+971">🇦🇪 +971</option>
                        <option value="+61">🇦🇺 +61</option>
                        <option value="+65">🇸🇬 +65</option>
                      </select>
                      <span className="vs-cc-caret" aria-hidden="true">
                        ▾
                      </span>
                    </div>

                    <input
                      type="tel"
                      inputMode="tel"
                      name="whatsapp"
                      placeholder="WhatsApp Number"
                      required
                    />
                  </div>
                </label>

                <label
                  className="vs-field"
                  data-aos="fade-up"
                  data-aos-delay="200"
                >
                  <span>Email</span>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    autoComplete="email"
                    inputMode="email"
                    required
                  />
                </label>

                <button
                  className="vs-btn"
                  type="submit"
                  data-aos="fade-up"
                  data-aos-delay="250"
                >
                  Join Varaha Family
                </button>
              </form>

              <small
                className="vs-powered"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                {poweredBy}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
